import React, { useState, useCallback } from 'react'
import { token } from '@atlaskit/tokens'
import Button from '@atlaskit/button'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { formatRelativeTime } from '../../utils/formatters'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'

/**
 * BUG ITEM COMPONENT
 *
 * Displays a single linked Jira bug with issue details and unlink action.
 *
 * Features:
 * - Issue key as clickable link to Jira
 * - Summary, status, priority, and issue type display
 * - Assignee name if available
 * - Linked by user and timestamp
 * - Unlink button with confirmation
 *
 * @param {object} bug - Bug object from API
 * @param {function} onUnlink - Callback when bug is unlinked (receives bugId)
 * @param {string} jiraBaseUrl - Base URL for Jira instance (optional)
 */
export function BugItem({ bug, onUnlink, jiraBaseUrl }) {
  // State for unlink loading
  const [isUnlinking, setIsUnlinking] = useState(false)

  // State for the Jira-styled unlink confirmation modal
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  /**
   * HANDLE UNLINK CLICK
   * Opens the confirmation modal.
   */
  const handleUnlinkClick = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  /**
   * HANDLE CONFIRM UNLINK
   * Unlinks the bug after user confirms via the modal.
   */
  const handleConfirmUnlink = useCallback(async () => {
    setIsUnlinking(true)
    try {
      await onUnlink(bug.id)
    } catch (error) {
      console.error('Failed to unlink bug:', error)
    } finally {
      setIsUnlinking(false)
    }
  }, [bug.id, onUnlink])

  /**
   * GET STATUS COLOR
   * Returns a color for the status badge based on common status names.
   */
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase()
    if (statusLower === 'done' || statusLower === 'closed' || statusLower === 'resolved') {
      return token('color.background.success', '#DFFCF0')
    }
    if (statusLower === 'in progress' || statusLower === 'in review') {
      return token('color.background.information', '#DEEBFF')
    }
    if (statusLower === 'blocked' || statusLower === 'on hold') {
      return token('color.background.warning', '#FFF7D6')
    }
    // Default - to do, open, etc.
    return token('color.background.neutral', '#F4F5F7')
  }

  /**
   * GET PRIORITY COLOR
   * Returns a color for the priority badge.
   */
  const getPriorityColor = (priority) => {
    const priorityLower = priority?.toLowerCase()
    if (priorityLower === 'highest' || priorityLower === 'blocker') {
      return token('color.text.danger', '#AE2E24')
    }
    if (priorityLower === 'high') {
      return token('color.text.warning', '#A54800')
    }
    if (priorityLower === 'low' || priorityLower === 'lowest') {
      return token('color.text.subtle', '#626F86')
    }
    // Medium or default
    return token('color.text', '#172B4D')
  }

  // Show loading state if unlinking
  if (isUnlinking) {
    return (
      <div
        className="bug-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          borderRadius: '4px',
          backgroundColor: token('color.background.neutral', '#F4F5F7'),
          marginBottom: '8px',
          opacity: 0.5,
          minHeight: '60px'
        }}
      >
        <Spinner size="small" />
        <span style={{ marginLeft: '8px', color: token('color.text.subtle') }}>Unlinking...</span>
      </div>
    )
  }

  // Build Jira issue URL
  // The jiraBaseUrl is typically the Jira instance URL (e.g., https://your-domain.atlassian.net)
  // If not provided, we'll just make the key text without a link
  const issueUrl = jiraBaseUrl ? `${jiraBaseUrl}/browse/${bug.jiraIssueKey}` : null

  // Handle case where Jira fetch failed
  const fetchFailed = bug.jiraFetchFailed

  return (
    <div
      className="bug-item"
      style={{
        padding: '12px',
        borderRadius: '4px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        marginBottom: '8px'
      }}
    >
      {/* Main content row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}
      >
        {/* Left: Issue type icon + Issue key + Summary */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 0 }}>
          {/* Issue type icon from Jira */}
          {bug.issueTypeIcon && (
            <img
              src={bug.issueTypeIcon}
              alt={bug.issueType || 'Issue'}
              style={{
                width: '16px',
                height: '16px',
                marginTop: '2px',
                flexShrink: 0
              }}
              onError={(e) => { e.target.style.display = 'none' }}
            />
          )}

          {/* Issue details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Issue key + Summary */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {/* Issue key - link if URL available */}
              {issueUrl ? (
                <a
                  href={issueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: token('color.link', '#0052CC'),
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => { e.target.style.textDecoration = 'underline' }}
                  onMouseLeave={(e) => { e.target.style.textDecoration = 'none' }}
                >
                  {bug.jiraIssueKey}
                </a>
              ) : (
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: token('color.text', '#172B4D')
                  }}
                >
                  {bug.jiraIssueKey}
                </span>
              )}

              {/* Issue type badge if available */}
              {bug.issueType && !fetchFailed && (
                <span
                  style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    backgroundColor: token('color.background.neutral.subtle', '#F1F2F4'),
                    color: token('color.text.subtle', '#5E6C84')
                  }}
                >
                  {bug.issueType}
                </span>
              )}
            </div>

            {/* Summary - truncate if too long */}
            {bug.summary && !fetchFailed && (
              <div
                style={{
                  fontSize: '13px',
                  color: token('color.text', '#172B4D'),
                  marginTop: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={bug.summary}
              >
                {bug.summary}
              </div>
            )}

            {/* Fetch failed message */}
            {fetchFailed && (
              <div
                style={{
                  fontSize: '12px',
                  color: token('color.text.subtle', '#5E6C84'),
                  marginTop: '4px',
                  fontStyle: 'italic'
                }}
              >
                Unable to load issue details from Jira
              </div>
            )}

            {/* Status + Priority + Assignee row */}
            {!fetchFailed && (bug.status || bug.priority || bug.assigneeDisplayName) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '6px',
                  flexWrap: 'wrap'
                }}
              >
                {/* Status badge */}
                {bug.status && (
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      backgroundColor: getStatusColor(bug.status),
                      color: token('color.text', '#172B4D'),
                      fontWeight: 500
                    }}
                  >
                    {bug.status}
                  </span>
                )}

                {/* Priority */}
                {bug.priority && (
                  <span
                    style={{
                      fontSize: '11px',
                      color: getPriorityColor(bug.priority)
                    }}
                  >
                    {bug.priority}
                  </span>
                )}

                {/* Assignee */}
                {bug.assigneeDisplayName && (
                  <span
                    style={{
                      fontSize: '11px',
                      color: token('color.text.subtle', '#5E6C84')
                    }}
                  >
                    · {bug.assigneeDisplayName}
                  </span>
                )}
              </div>
            )}

            {/* Linked by info */}
            {bug.linkedOn && (
              <div
                style={{
                  fontSize: '11px',
                  color: token('color.text.subtlest', '#7A869A'),
                  marginTop: '6px'
                }}
              >
                Linked {formatRelativeTime(bug.linkedOn)}
              </div>
            )}
          </div>
        </div>

        {/* Right: Unlink button */}
        {onUnlink && (
          <Tooltip content="Unlink bug">
            <Button
              appearance="subtle"
              spacing="compact"
              onClick={handleUnlinkClick}
              iconBefore={<CrossIcon size="small" label="Unlink" />}
            />
          </Tooltip>
        )}
      </div>
      {/* Unlink confirmation modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmUnlink}
        title="Unlink Bug"
        itemName={bug.jiraIssueKey}
        itemType="bug"
        warningMessage="This bug will be unlinked from the execution. This action cannot be undone."
      />
    </div>
  )
}

export default BugItem
